import React, { useEffect, useState } from 'react';
import { CustomTextInput, RadioGroup } from '../FormElements';

export const INPUT_TYPE_OPTIONS = [
    { value: 'text_input', label: 'Text input' },
    { value: 'drop_down', label: 'Dropdown' },
    { value: 'radio', label: 'Radio' },
    { value: 'date_picker', label: 'Date picker' },
    { value: 'mobile_input', label: 'Mobile input' },
];

export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

const KNOWN_INPUT_TYPES = INPUT_TYPE_OPTIONS.map((item) => item.value);

export const createOptionRow = (label = "", source = null) => ({
    key: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    label,
    source
});

export const parseComponentConfig = (config) => {
    if (!config) {
        return {};
    }
    if (typeof config === "string") {
        try {
            const parsed = JSON.parse(config);
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch {
            return {};
        }
    }
    return typeof config === "object" ? config : {};
};

const getOptionLabel = (item) => {
    if (typeof item === "string") {
        return item.trim();
    }
    return (item?.label || "").trim();
};

export const getInitialOptionRows = (component, config) => {
    const supportsOptions = component === "drop_down" || component === "radio";
    if (!supportsOptions || !Array.isArray(config.options)) {
        return [createOptionRow()];
    }

    const savedOptions = config.options
        .map((item) => {
            const label = getOptionLabel(item);
            if (!label) {
                return null;
            }
            const source = item && typeof item === "object" ? item : null;
            return createOptionRow(label, source);
        })
        .filter(Boolean);

    if (savedOptions.length === 0) {
        return [createOptionRow()];
    }

    return savedOptions;
};

const getOriginalComponentConfig = (fieldData) => {
    const config = fieldData?.component_config;
    if (config == null || config === "") {
        return null;
    }
    if (typeof config === "string") {
        const parsed = parseComponentConfig(config);
        return Object.keys(parsed).length ? parsed : null;
    }
    return config;
};

export const useFieldInputConfig = (fieldData) => {
    const isEditable = fieldData?.is_editable !== false;
    const initialConfig = parseComponentConfig(fieldData?.component_config);
    const initialComponent = fieldData?.component || "text_input";

    const [componentType, setComponentType] = useState(initialComponent);
    const [inputTypeError, setInputTypeError] = useState("");
    const [optionRows, setOptionRows] = useState(getInitialOptionRows(initialComponent, initialConfig));
    const [optionsError, setOptionsError] = useState("");

    useEffect(() => {
        setInputTypeError("");
        setOptionsError("");
    }, [componentType, optionRows]);

    const handleInputTypeChange = (value) => {
        setComponentType(value);
        if (value === "radio" || value === "drop_down") {
            setOptionRows((current) => (current.length ? current : [createOptionRow()]));
        }
    };

    const handleOptionChange = (key, value) => {
        setOptionRows((current) => current.map((row) => (
            row.key === key ? { ...row, label: value } : row
        )));
    };

    const handleAddOption = () => {
        setOptionRows((current) => [...current, createOptionRow()]);
    };

    const handleRemoveOption = (key) => {
        setOptionRows((current) => (
            current.length <= 1 ? current : current.filter((row) => row.key !== key)
        ));
    };

    const isOptionsVisible = componentType === "radio" || componentType === "drop_down";

    const getTrimmedOptions = () => {
        return optionRows.map((row) => {
            const label = (row.label || "").trim();
            if (row.source && typeof row.source === "object") {
                return { ...row.source, label };
            }
            return { label };
        });
    };

    const validateCustomOptions = () => {
        const trimmedOptions = getTrimmedOptions();

        if (optionRows.length === 0 || trimmedOptions.length === 0) {
            setOptionsError("Add at least one option");
            return false;
        }

        if (trimmedOptions.some((item) => !item.label)) {
            setOptionsError("Every option needs a label");
            return false;
        }

        const labels = trimmedOptions.map((item) => item.label.toLowerCase());
        if (new Set(labels).size !== labels.length) {
            setOptionsError("Options must be unique");
            return false;
        }

        return true;
    };

    const buildComponentConfig = () => {
        if (!isEditable) {
            return getOriginalComponentConfig(fieldData);
        }

        if (!KNOWN_INPUT_TYPES.includes(componentType)) {
            return getOriginalComponentConfig(fieldData);
        }

        if (componentType === "text_input" || componentType === "mobile_input") {
            return null;
        }

        if (componentType === "date_picker") {
            return { type: "date_picker" };
        }

        if (componentType === "drop_down" || componentType === "radio") {
            return {
                type: componentType,
                options: getTrimmedOptions()
            };
        }

        return null;
    };

    const validateComponentConfig = () => {
        if (!isEditable) {
            return true;
        }

        let is_valid = true;

        if (!componentType) {
            setInputTypeError("Select an input type");
            is_valid = false;
        }

        if (isOptionsVisible && !validateCustomOptions()) {
            is_valid = false;
        }

        return is_valid;
    };

    const InputConfigFields = !isEditable ? null : (
        <>
            <RadioGroup
                label="Input type"
                require
                labelStyle="e-font-weight-400"
                data={INPUT_TYPE_OPTIONS}
                selectedItem={componentType}
                onSelectItem={handleInputTypeChange}
                className="pb-1"
            />
            {inputTypeError &&
                <p className="mb-3 e-error-message e-primary-font e-font-10 line-height-12px">
                    {inputTypeError}
                </p>
            }

            {/* Date picker fields: Format, Min date, Max date
            {componentType === "date_picker" &&
                <>
                    <CustomTextInput
                        label="Format"
                        placeholder={DEFAULT_DATE_FORMAT}
                        type="text"
                        value=""
                        className="w-100"
                        labelStyle="e-font-weight-400"
                        handleChange={() => {}}
                    />
                    <CustomTextInput
                        label="Min date"
                        placeholder="1900-01-01"
                        type="text"
                        value=""
                        className="w-100"
                        labelStyle="e-font-weight-400"
                        handleChange={() => {}}
                    />
                    <CustomTextInput
                        label="Max date"
                        placeholder="today"
                        type="text"
                        value=""
                        className="w-100"
                        labelStyle="e-font-weight-400"
                        handleChange={() => {}}
                    />
                </>
            }
            */}

            {isOptionsVisible &&
                <div className="mb-3">
                    <label className="e-primary-font e-text-rich-black e-font-weight-400 line-height-18px e-font-14 padding-6px-bottom">
                        Options
                    </label>
                    {optionRows.map((row) => (
                        <div className="d-flex align-items-start gap-2" key={row.key}>
                            <div className="flex-grow-1">
                                <CustomTextInput
                                    placeholder="Enter option"
                                    type="text"
                                    value={row.label}
                                    className="w-100"
                                    labelStyle="e-font-weight-400"
                                    handleChange={(value) => handleOptionChange(row.key, value)}
                                />
                            </div>
                            {optionRows.length > 1 &&
                                <span className="cursor-pointer e-edit-btn e-primary-font e-font-weight-500 e-font-14 e-text-primary-blue pt-3"
                                    onClick={() => handleRemoveOption(row.key)}>
                                    Remove
                                </span>
                            }
                        </div>
                    ))}
                    <div className="d-flex align-items-center cursor-pointer mt-1 mb-2"
                        onClick={handleAddOption}>
                        <img draggable="false"
                            src={require("../../Assets/Images/Home/add-icon.svg").default}
                            className="me-1"
                            alt="add-option" />
                        <span className="e-font-weight-600 e-font-14 e-primary-font e-text-primary-blue">
                            Add Option
                        </span>
                    </div>
                    {optionsError &&
                        <p className="mb-0 e-error-message e-primary-font e-font-10 line-height-12px">
                            {optionsError}
                        </p>
                    }
                </div>
            }
        </>
    );

    return {
        componentType,
        buildComponentConfig,
        validateComponentConfig,
        InputConfigFields
    };
};
